import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabaseClient';
import { homeFor, StaffContext, useStaffGate } from '../lib/staffContext';
import { StaffShell } from '../ui/StaffShell';
import { ContextError } from '../ui/ContextError';
import { useI18n } from '../i18n';
import { useAnnounce } from '../ui/a11y';
import { adminNav } from './admin/adminData';
import { errorText, fmtDate, fmtDateTime, Notice, rpc } from './admin/api';
import { roleLabel } from '../ui/roles';
import type { Strings } from '../i18n/strings';

// Notification centre (spec D, "Notification centre"): every change that
// affects the person — roles, assignments, approvals, school notices — with
// the date. Reached from the bell on every staff screen.
export function Notifications() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const { ctx, error } = useStaffGate(['platform_admin', 'school_admin', 'head_of_school', 'counsellor', 'teacher', 'municipality_coordinator']);

  async function signOut() {
    await supabase.auth.signOut();
    navigate('/teacher/login');
  }

  if (error) return <ContextError message={error} />;
  if (!ctx || !ctx.active_role) return <main className="page-loading" role="status">{t.loading}</main>;

  const nav =
    ctx.active_role === 'platform_admin'
      ? adminNav(t, 'overview').map((n) => ({ ...n, active: false }))
      : [{ icon: '🏠', label: t['notif.home'], href: homeFor(ctx.active_role) }];

  return (
    <StaffShell role={roleLabel(t, ctx.active_role)} nav={nav} title={t['notif.title']} who={ctx.display_name} ctx={{ ...ctx, unread_notifications: 0 }} onSignOut={signOut}>
      <NotificationList ctx={ctx} />
    </StaffShell>
  );
}

function text(t: Strings, locale: string, n: Notice): string {
  const b = n.body as Record<string, string | number | boolean | undefined>;
  const role = typeof b.role === 'string' ? t[`pa.roleName_${b.role as 'teacher'}`] ?? b.role : '';
  switch (n.kind) {
    case 'role_added':
      return t['notif.roleAdded'](role);
    case 'role_removed':
      return t['notif.roleRemoved'](role);
    case 'approval_waiting':
      return b.as_delegate ? t['notif.approvalWaitingDelegate'](role) : t['notif.approvalWaiting'](role);
    case 'approval_expired':
      return t['notif.approvalExpired'](role);
    case 'approval_decided_other':
      return b.by_delegate
        ? b.approved
          ? t['notif.delegateApproved'](role)
          : t['notif.delegateRejected'](role)
        : b.approved
          ? t['notif.primaryApproved'](role)
          : t['notif.primaryRejected'](role);
    case 'delegate_set':
      return typeof b.ends_on === 'string' ? t['notif.delegateSetUntil'](fmtDate(locale, b.ends_on)) : t['notif.delegateSet'];
    case 'delegate_ended':
      return t['notif.delegateEnded'];
    case 'deputy_scope_changed': {
      const grades = (n.body as { grades?: unknown }).grades;
      return b.whole_school || !Array.isArray(grades) ? t['notif.scopeWhole'] : t['notif.scopeGrades'](grades.join(', '));
    }
    case 'primary_head_changed':
      return t['notif.primaryChanged'];
    case 'approval_no_primary_head':
      return t['notif.approvalNoHead'](role);
    case 'approval_decided':
      return b.approved ? t['notif.approvalApproved'] : t['notif.approvalRejected'];
    case 'erasure_needs_second_admin':
      return t['notif.eraseSecond'];
    case 'primary_appointment_needs_second_admin':
      return t['notif.appointSecond'];
    case 'assigned':
      return t['notif.assigned'](Number(b.count ?? 1));
    case 'unassigned':
      return t['notif.unassigned'];
    case 'primary_head':
      return t['notif.primaryHead'];
    case 'paperwork_changed':
      return t['notif.paperwork'](String(b.status ?? ''));
    case 'school_live':
      return t['notif.schoolLive'];
    case 'dedicated_requested':
      return t['notif.dedicatedRequested'];
    case 'dedicated_cancelled':
      return t['notif.dedicatedCancelled'];
    case 'dedicated_pending':
      return t['notif.dedicatedPending'];
    case 'dedicated_ready':
      return t['notif.dedicatedReady'];
    case 'person_reactivated':
      return t['notif.reactivated'];
    case 'language_removed': {
      const langs = (n.body as { languages?: unknown }).languages;
      return t['r4.n.language_removed'](Array.isArray(langs) ? langs.map((x) => String(x).toUpperCase()).join(', ') : '');
    }
    case 'deputy_help_requested':
      return t['r4.n.deputy_help_requested'](String(b.by ?? '')) + (b.note ? ` “${String(b.note)}”` : '');
    case 'approval_reminder':
      return t['r4.n.approval_reminder'](Number(b.days_left ?? 0));
    case 'test_account_expiring':
      return b.name ? t['r4.n.test_account_expiring_admin'](String(b.name), Number(b.days ?? 0)) : t['r4.n.test_account_expiring'](Number(b.days ?? 0));
    case 'test_expiry_blocked':
      return t['r4.n.test_expiry_blocked'](String(b.name ?? ''));
    case 'early_signal':
      return t['r3.notif.signal'](t[`topic.${String(b.topic)}` as 'topic.exams'] ?? String(b.topic ?? ''));
    default:
      return n.kind;
  }
}

// Head of school notices about approvals, deputies and delegation open the head's home.
const HEAD_KINDS = new Set(['approval_waiting', 'approval_reminder', 'approval_decided_other', 'delegate_set', 'deputy_scope_changed', 'primary_head']);

function linkFor(ctx: StaffContext, n: Notice): string | null {
  if (ctx.active_role === 'head_of_school') return n.kind === 'early_signal' ? '/school/results' : HEAD_KINDS.has(n.kind) ? '/school' : null;
  if (ctx.active_role !== 'platform_admin') return null;
  if (n.kind === 'approval_no_primary_head' || n.kind === 'erasure_needs_second_admin' || n.kind === 'primary_appointment_needs_second_admin') {
    return '/admin/approvals';
  }
  return n.school_id ? `/admin/schools/${n.school_id}` : null;
}

function NotificationList({ ctx }: { ctx: StaffContext }) {
  const { t, locale } = useI18n();
  const announce = useAnnounce();
  const [rows, setRows] = useState<Notice[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    const r = await rpc<Notice[]>('my_notifications', { p_limit: 100 });
    if (r.error) setError(errorText(t, r.error));
    else setRows(r.data);
  }, [t]);

  useEffect(() => {
    load();
  }, [load]);

  async function markAll() {
    const r = await rpc<number>('mark_notifications_read', { p_ids: null });
    if (r.error) return setError(errorText(t, r.error));
    announce(t['notif.allRead']);
    await load();
  }

  const unread = rows?.filter((r) => !r.read_at).length ?? 0;

  return (
    <section className="panel p5" aria-labelledby="notif-list">
      <div className="panel-head wrap">
        <h2 className="panel-title" id="notif-list">
          {unread > 0 ? t['notif.unreadN'](unread) : t['notif.allCaughtUp']}
        </h2>
        {unread > 0 && (
          <button type="button" className="btn btn-ghost" onClick={markAll}>
            {t['notif.markAll']}
          </button>
        )}
      </div>
      {error ? (
        <p className="error-text" role="alert">
          {error}
        </p>
      ) : !rows ? (
        <p role="status" className="text-muted">
          {t.loading}
        </p>
      ) : rows.length === 0 ? (
        <p className="text-muted">{t['notif.empty']}</p>
      ) : (
        <ul className="notif-list">
          {rows.map((n) => {
            const to = linkFor(ctx, n);
            return (
              <li key={n.id} className={n.read_at ? '' : 'unread'}>
                <span className="notif-dot" aria-hidden="true" />
                <span className="notif-body">
                  {!n.read_at && <span className="visually-hidden">{t['notif.new']}: </span>}
                  {to ? <Link to={to}>{text(t, locale, n)}</Link> : text(t, locale, n)}
                  <span className="text-muted">
                    {n.school_name ? ` · ${n.school_name}` : ''} · {fmtDateTime(locale, n.created_at)}
                  </span>
                </span>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
